

import { useState } from 'react';
import { Alert, Modal, Upload } from 'antd';
import { BookyButton } from '../../Buttons/BookyButton';
import { WebApiMemberDtoAddMemberObjectDto, WebApiMemberDtoGroupDto, WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto, WebApiServicesDataTransferObjectsGroupServiceGroupResultDto, WebApiMemberFieldsDtoMemberUploadFieldDto, WebApiMemberDtoMemberDto } from '../../../Repository/eventbookyapi';
import { SubscriptionLevel } from '../../../Types/SubscriptionLevel';
import { TextService } from '../../../Services/TextService';
import { FileOutlined } from '@ant-design/icons';
import { MemberService } from '../../../Services/MemberService';
import { UploadMemberPreviewData } from './UploadMemberPreviewTable';
import { isEmail } from 'react-multi-email';
import { mapUploadFieldsToBulkMemberDto } from '../../../Mappers/mapUploadFieldsToBulkMemberDto';
import { AuthenticatedUser } from '../../../Types/AuthenticatedUser';
const xlsx = require('xlsx');

const enum UploadErrorType { Unspecified, EmptyFile, FreeTooManyUsers, PremiumTooManyUsers }

interface UploadMemberButtonProps {
    uploadTemplateFields: WebApiMemberFieldsDtoMemberUploadFieldDto[];
    groups: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto[];
    members: WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto[];
    user: AuthenticatedUser;
    totalMemberCount: number;
}

type SkippedMemberReason = 'duplicate_id_in_file' | 'duplicate_email_in_file' | 'existing_id' | 'existing_email' | 'invalid_email';

interface SkippedMember {
    uniqueId: string;
    email?: string | null;
    reason: SkippedMemberReason;
}

export const UploadMemberButton = (props: UploadMemberButtonProps) => {
    const [members, setMembers] = useState([] as WebApiMemberDtoAddMemberObjectDto[]);
    const [validMembers, setValidMembers] = useState([] as WebApiMemberDtoAddMemberObjectDto[]);
    const [skippedMembers, setSkippedMembers] = useState([] as SkippedMember[]);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(UploadErrorType.Unspecified as UploadErrorType);
    const [isLoading, setIsLoading] = useState(false);
    const [errorText, setErrorText] = useState("");
    const [successText, setSuccessText] = useState("");

    const GetText = TextService.GetText;

    const CloseModalAndClearState = () => {
        setOpen(false);
        setError(UploadErrorType.Unspecified);
        setIsLoading(false);
        setSuccessText(GetText("Common_UploadComplete"));
    }

    const CloseModalAndShowNothing = () => {
        setOpen(false);
        setError(UploadErrorType.Unspecified);
        setIsLoading(false);
    }

    const showError = (error: UploadErrorType) => {
        let text = "";
        switch (error) {
            case UploadErrorType.EmptyFile:
                text = GetText("UploadMembers_WrongFormat"); break;
            case UploadErrorType.FreeTooManyUsers:
                text = GetText("UploadMembers_Found") + members.length + GetText("UploadMembers_ErrorMoreThenMaxMembersInFree1") + GetText("UploadMembers_ErrorMoreThenMaxMembersInFree2");
                break;
            case UploadErrorType.PremiumTooManyUsers:
                text = GetText("UploadMembers_Found") + members.length + GetText("UploadMembers_ErrorMoreThenMaxMembersInPremium");
                break;
        }

        setErrorText(text);
    }

    const onFileUpload = (event: any, reader: any) => {
        var text = reader.result?.toString();

        const { result } = event.target;
        const workbook = xlsx.read(result, { type: "binary" });

        var allLines = [];
        if (workbook && workbook.SheetNames) {
            for (const Sheet in workbook.Sheets) {
                if (workbook.Sheets.hasOwnProperty(Sheet)) {
                    text = xlsx.utils.sheet_to_csv(workbook.Sheets[Sheet], { FS: ";" });
                }
            }
        }

        allLines = text!.split((/\r|\n|\r/));

        if (!allLines) {
            showError(UploadErrorType.EmptyFile);
            return;
        }
        const headlines: string[] = allLines[0].split(';');

        allLines.shift();

        if (allLines.length === 0) {
            showError(UploadErrorType.EmptyFile);
            return;
        }

        const membersArray: WebApiMemberDtoAddMemberObjectDto[] = [];
        const validMembersArray: WebApiMemberDtoAddMemberObjectDto[] = [];
        const skippedMembersArray: SkippedMember[] = [];



        allLines.forEach(function (line: any) {
            const memberInFile: string[] = line.split(';');

            if (!memberInFile[0] && !memberInFile[1] && !memberInFile[2] && !memberInFile[3]) {
                return;
            }

            const member = mapUploadFieldsToBulkMemberDto(props.uploadTemplateFields, memberInFile, headlines, props.groups);

            let shouldSkip = false;

            // User is already added from previous excel line
            if (membersArray.filter(p => p.uniqueId == member.uniqueId).length > 0) {
                skippedMembersArray.push({ uniqueId: member.uniqueId!, email: member.email, reason: 'duplicate_id_in_file' });
                shouldSkip = true;
            }
            // Email is already added from previous excel line
            else if (member.email!.length > 0 && membersArray.filter(p => p.email == member.email).length > 0) {
                skippedMembersArray.push({ uniqueId: member.uniqueId!, email: member.email, reason: 'duplicate_email_in_file' });
                shouldSkip = true;
            }
            // uniqueid already exist from previous added members
            else if (props.members.filter((m: WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto) => m.uniqueId == member.uniqueId).length > 0) {
                skippedMembersArray.push({ uniqueId: member.uniqueId!, email: member.email, reason: 'existing_id' });
                shouldSkip = true;
            }
            // Email already exist from previous added members (FIXED: was checking uniqueId instead of email)
            else if (member.email!.length > 0 && props.members.filter((m: WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto) => m.email == member.email).length > 0) {
                skippedMembersArray.push({ uniqueId: member.uniqueId!, email: member.email, reason: 'existing_email' });
                shouldSkip = true;
            }
            // Invalid email check
            else if (member.email!.length > 0 && !isEmail(member.email!)) {
                skippedMembersArray.push({ uniqueId: member.uniqueId!, email: member.email, reason: 'invalid_email' });
                shouldSkip = true;
            }

            // Always add to membersArray to show in preview table
            membersArray.push(member);

            // Only add to validMembersArray if no validation errors
            if (!shouldSkip) {
                validMembersArray.push(member);
            }
        });

        if (props.user.subscriptionLevel == SubscriptionLevel.Free && (allLines.length + props.totalMemberCount) > 25) {
            showError(UploadErrorType.FreeTooManyUsers);
            return;
        };

        if (user.hasPaidSubscription && (allLines.length + props.totalMemberCount) > 999) {
            showError(UploadErrorType.PremiumTooManyUsers);
            return;
        };

        setMembers(membersArray);
        setValidMembers(validMembersArray);
        setSkippedMembers(skippedMembersArray);
        setOpen(true);
    };


    return (
        <>
            <Upload
                accept=".txt, .csv, .xlsx"
                showUploadList={false}
                beforeUpload={file => {
                    const reader = new FileReader();

                    reader.onload = async e => {
                        onFileUpload(e, reader);
                    };
                    reader.readAsBinaryString(file);
                    return false;
                }}
            >
                <BookyButton type="primary" style={{ width: "250px" }} icon={<FileOutlined />} text={GetText("Common_ChooseFile")}>
                </BookyButton>

            </Upload>
            <Modal
                open={open}
                title={GetText("UploadMembers_ModalTitlePreview")}
                okText={error == UploadErrorType.Unspecified ? GetText("UploadMembers_ModalAdd") + members.length + GetText("UploadMembers_ModalMembers") : GetText("UploadMembers_ModalOk")}
                cancelText={GetText("UploadMembers_ModalCancel")}
                width={1800}
                confirmLoading={isLoading}
                onCancel={() => { CloseModalAndShowNothing(); }}
                onOk={async () => {
                    setIsLoading(true);
                    if (validMembers.length > 0 && error == UploadErrorType.Unspecified) {
                        await MemberService.AddUsersInBulk({ members: validMembers });
                        CloseModalAndClearState();
                    }
                    CloseModalAndClearState();
                }}
            >
                {error == UploadErrorType.Unspecified ?
                    <>
                        <div style={{ marginBottom: "15px" }}>
                            <i>{GetText("UploadMembers_FoundInFile1")} {members.length} {GetText("UploadMembers_FoundInFile2")}</i>
                            {skippedMembers.length > 0 && (
                                <Alert
                                    style={{ marginTop: "10px" }}
                                    type="warning"
                                    showIcon
                                    message={
                                        <div>
                                            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                                {GetText("UploadMembers_SkippedMembersTitle").replace("{count}", skippedMembers.length.toString())}
                                            </div>
                                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                                {skippedMembers.filter(s => s.reason === 'invalid_email').length > 0 && (
                                                    <li>{GetText("UploadMembers_InvalidEmailCount").replace("{count}", skippedMembers.filter(s => s.reason === 'invalid_email').length.toString())}</li>
                                                )}
                                                {skippedMembers.filter(s => s.reason === 'duplicate_id_in_file').length > 0 && (
                                                    <li>{GetText("UploadMembers_DuplicateIdInFile").replace("{count}", skippedMembers.filter(s => s.reason === 'duplicate_id_in_file').length.toString())}</li>
                                                )}
                                                {skippedMembers.filter(s => s.reason === 'duplicate_email_in_file').length > 0 && (
                                                    <li>{GetText("UploadMembers_DuplicateEmailInFile").replace("{count}", skippedMembers.filter(s => s.reason === 'duplicate_email_in_file').length.toString())}</li>
                                                )}
                                                {skippedMembers.filter(s => s.reason === 'existing_id').length > 0 && (
                                                    <li>{GetText("UploadMembers_ExistingId").replace("{count}", skippedMembers.filter(s => s.reason === 'existing_id').length.toString())}</li>
                                                )}
                                                {skippedMembers.filter(s => s.reason === 'existing_email').length > 0 && (
                                                    <li>{GetText("UploadMembers_ExistingEmail").replace("{count}", skippedMembers.filter(s => s.reason === 'existing_email').length.toString())}</li>
                                                )}
                                            </ul>
                                        </div>
                                    }
                                />
                            )}
                        </div>
                        <UploadMemberPreviewData
                            uploadTemplateFields={props.uploadTemplateFields}
                            members={members}
                            skippedMembers={skippedMembers}
                        />
                    </>
                    : ""}
            </Modal>
            {errorText ? <Alert style={{ marginTop: "20px" }} showIcon={true} type="error" message={errorText}></Alert> : ""}
            {successText ? <Alert style={{ marginTop: "20px" }} showIcon={true} type="success" message={successText}></Alert> : ""}
        </>
    );
};

export default UploadMemberButton;